import React, { useEffect } from 'react';
import { ModalProps } from '../../types/modal';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    InputLabel,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { SetMenu } from '../../types/set-menu';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import FormCheckbox from '../../shared/FormCheckbox';
import FormMultiAutocomplete from '../../shared/FormMultiAutocomplete';
import SetMenuItems from './SetMenuItems';
import {
    useCreateSetMenuMutation,
    useGetAllAssetsQuery,
    useUpdateSetMenuMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import toast from '../../shared/Toast';
import { SetMenuCourse } from '../../enums/set-menu';
import { ServiceType } from '../../enums/contractor';

const Schema = z.object({
    serviceType: z.nativeEnum(ServiceType),
    name: z.string().min(1, { message: 'Required' }),
    description: z.string().optional(),
    pricePerPerson: z.coerce
        .number()
        .multipleOf(0.01, { message: 'Invalid currency format.' })
        .min(1, { message: 'Price must be at least A$1.00' }),
    isActive: z.boolean(),
    moduleIds: z.array(z.number()),
    items: z
        .array(
            z.object({
                id: z.number().optional(),
                name: z.string().min(1, { message: 'Required' }),
                description: z.string().optional(),
                course: z.nativeEnum(SetMenuCourse),
                order: z.number(),
            }),
        )
        .min(1, { message: 'At least 1 item required' }),
});

export type SetMenuFormValues = z.infer<typeof Schema>;

const TYPE_OPTIONS = [
    { value: ServiceType.Catering, label: 'Catering' },
    { value: ServiceType.Alcohol, label: 'Alcohol' },
];

const defaultValues: SetMenuFormValues = {
    serviceType: ServiceType.Catering,
    name: '',
    description: '',
    pricePerPerson: 0,
    isActive: true,
    moduleIds: [],
    items: [],
};

interface SetMenuModalProps extends ModalProps {
    setMenu: SetMenu | null;
    onSaved?: (setMenu: SetMenu) => void;
}

const SetMenuModal = ({
    setMenu,
    open,
    onClose,
    onSaved,
}: SetMenuModalProps) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const methods = useForm<SetMenuFormValues>({
        defaultValues,
        resolver: zodResolver(Schema),
    });

    const { data: modules } = useGetAllAssetsQuery(
        {
            url: `/owners/${loggedInUser?.company}/modules`,
            status: 'published',
        },
        { skip: !loggedInUser?.company },
    );

    const moduleOptions =
        modules?.map((module: any) => ({
            id: module.id,
            name: module.name,
        })) ?? [];

    const [createSetMenu] = useCreateSetMenuMutation();
    const [updateSetMenu] = useUpdateSetMenuMutation();

    const { control, handleSubmit, reset } = methods;

    const onSubmit = async (data: SetMenuFormValues) => {
        try {
            if (!loggedInUser?.company) return;

            const payload: SetMenu = {
                serviceType: data.serviceType,
                name: data.name,
                description: data.description,
                pricePerPerson: data.pricePerPerson,
                isActive: data.isActive,
                items: data.items.map((item, index) => ({
                    ...item,
                    order: index,
                })),
                modules: data.moduleIds.map((id) => ({ id })),
            };

            let saved: SetMenu;
            if (setMenu?.id !== undefined) {
                saved = await updateSetMenu([setMenu.id, payload]).unwrap();
            } else {
                saved = await createSetMenu(payload).unwrap();
            }

            toast.success('Set menu saved successfully');
            reset(defaultValues);
            onSaved?.(saved);
        } catch {
            toast.error('Error saving set menu');
        }
    };

    const handleClose = () => {
        onClose();
        reset(defaultValues);
    };

    useEffect(() => {
        if (setMenu?.id !== undefined) {
            reset({
                serviceType: setMenu.serviceType,
                name: setMenu.name,
                description: setMenu.description ?? '',
                pricePerPerson: setMenu.pricePerPerson,
                isActive: setMenu.isActive,
                moduleIds: setMenu.modules?.map((module) => module.id) ?? [],
                items: setMenu.items.map((item) => ({
                    id: item.id,
                    name: item.name,
                    description: item.description ?? '',
                    course: item.course,
                    order: item.order,
                })),
            });
        } else {
            reset(defaultValues);
        }
    }, [setMenu, open]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth='md'
            fullScreen={isMobile || isIpad}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Set Menu
                        <IconButton
                            onClick={handleClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{ maxHeight: '600px', overflow: 'auto' }}
                    >
                        <Stack spacing={2}>
                            <Box>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 600, mb: 1 }}
                                >
                                    Menu Details
                                </Typography>
                                <Stack spacing={2}>
                                    <Box>
                                        <InputLabel required>Type</InputLabel>
                                        <FormSelect
                                            name='serviceType'
                                            control={control}
                                            options={TYPE_OPTIONS}
                                        />
                                    </Box>
                                    <Box>
                                        <InputLabel
                                            required
                                            htmlFor='set-menu-name__input'
                                        >
                                            Menu Name
                                        </InputLabel>
                                        <FormTextField
                                            id='set-menu-name__input'
                                            name='name'
                                            control={control}
                                            placeholder='Seafood BBQ Banquet'
                                        />
                                    </Box>
                                    <Box>
                                        <InputLabel htmlFor='set-menu-description__input'>
                                            Description
                                        </InputLabel>
                                        <FormTextField
                                            id='set-menu-description__input'
                                            name='description'
                                            control={control}
                                            placeholder='A shared banquet of grilled seafood, salads and dessert.'
                                            multiline
                                            rows={2}
                                        />
                                    </Box>
                                    <Box>
                                        <InputLabel
                                            required
                                            htmlFor='set-menu-price__input'
                                        >
                                            Price per person (ex. GST)
                                        </InputLabel>
                                        <FormTextField
                                            id='set-menu-price__input'
                                            name='pricePerPerson'
                                            type='number'
                                            placeholder='95.00'
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Typography>
                                                            $
                                                        </Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            control={control}
                                        />
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 600, mb: 1 }}
                                >
                                    Availability
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <FormCheckbox
                                            id='isActive__input'
                                            name='isActive'
                                            control={control}
                                        />
                                    }
                                    label='Active'
                                    htmlFor='isActive__input'
                                    sx={{ px: 1 }}
                                />
                                <FormHelperText>
                                    Turn this off to hide the set menu from
                                    guests without deleting it.
                                </FormHelperText>
                                <Box sx={{ mt: 2 }}>
                                    <InputLabel>Available on</InputLabel>
                                    <FormMultiAutocomplete
                                        name='moduleIds'
                                        control={control}
                                        options={moduleOptions}
                                        getOptionValue={(opt) => opt.id}
                                        getOptionLabel={(opt) => opt.name}
                                        placeholder='Select experiences'
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Menu items
                                </Typography>
                                <FormHelperText>
                                    Add the dishes or drinks included in this
                                    set menu.
                                </FormHelperText>
                            </Box>
                            <SetMenuItems />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <CustomButton
                            color='error'
                            type='button'
                            variant='contained'
                            onClick={handleClose}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type='submit'
                            variant='contained'
                            color='primary'
                        >
                            Save
                        </CustomButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default SetMenuModal;
